/**
 * Printable badge windows (FEA-22 member view; FEA-23 staff print actions,
 * docs/FRONTEND-PLAN.md §2.5).
 *
 * Every badge the backend renders (`GET …/badge`, `POST …/badges/print-batch`)
 * is a complete, self-contained HTML document (own <html>/<head>/<style>),
 * sized for a 4in x 3in card — see src/services/events/badge-template.js.
 * `openHtmlDocument` opens one in a new tab via `document.write` (the same
 * mechanism FEA-22's "View my badge" used); `autoPrint` additionally calls
 * `window.print()` once the document has finished laying out, guarded
 * against a closed/blocked window.
 */

/**
 * @param {string} html  A complete HTML document to display.
 * @param {{autoPrint?: boolean}} [options]
 * @returns {Window|null}  The opened window, or null if the browser blocked
 *   the pop-up (callers should toast an "allow pop-ups" message in that case).
 */
export function openHtmlDocument(html, { autoPrint = false } = {}) {
  const win = window.open('', '_blank', 'noopener');
  if (!win) return null;

  win.document.write(html);
  win.document.close();

  if (autoPrint) {
    const triggerPrint = () => {
      if (win.closed) return;
      try {
        win.focus();
        win.print();
      } catch {
        // Best-effort — a blocked/closed window should never throw upward.
      }
    };
    // document.write is synchronous, so `readyState` is frequently already
    // 'complete' by the time we get here (no external resources to wait
    // on); a short delay still lets the browser finish layout before print.
    if (win.document.readyState === 'complete') {
      setTimeout(triggerPrint, 50);
    } else {
      win.addEventListener('load', () => setTimeout(triggerPrint, 50), { once: true });
    }
  }

  return win;
}

const BODY_RE = /<body[^>]*>([\s\S]*)<\/body>/i;
const STYLE_RE = /<style[^>]*>([\s\S]*?)<\/style>/i;

/**
 * Pull the `<style>` contents and `<body>` inner HTML out of one badge's
 * self-contained document (see module doc). Both extractions are required
 * for the badge to render/print correctly; either failing is treated as a
 * whole-badge extraction failure so the caller can skip it.
 *
 * @returns {{style: string, body: string}|null}
 */
export function extractBadgeFragment(html) {
  const bodyMatch = BODY_RE.exec(html || '');
  const styleMatch = STYLE_RE.exec(html || '');
  if (!bodyMatch || !styleMatch) return null;
  return { style: styleMatch[1], body: bodyMatch[1] };
}

/**
 * Build one printable document from a batch of badge HTML documents
 * (`POST …/badges/print-batch` → `{badges: [{registrationId, html}]}`),
 * each wrapped in its own page (`page-break-after: always`) so a single
 * `window.print()` walks the whole roster. Every badge in one event shares
 * the same template, so the first successfully-extracted `<style>` block is
 * reused for all of them; badges whose HTML doesn't match the expected
 * `<body>`/`<style>` shape are skipped and counted as failures rather than
 * failing the whole batch.
 *
 * @param {Array<{registrationId: string, html: string}>} badges
 * @returns {{html: string|null, rendered: number, failed: number}}
 */
export function buildBadgeBatchDocument(badges) {
  let style = null;
  const bodies = [];
  let failed = 0;

  for (const badge of badges || []) {
    const fragment = extractBadgeFragment(badge && badge.html);
    if (!fragment) {
      failed += 1;
      continue;
    }
    if (style === null) style = fragment.style;
    bodies.push(fragment.body);
  }

  if (bodies.length === 0) {
    return { html: null, rendered: 0, failed };
  }

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Event badges</title>
<style>
${style || ''}
.badge-batch-page { page-break-after: always; }
.badge-batch-page:last-child { page-break-after: auto; }
</style>
</head>
<body>
${bodies.map((body) => `<div class="badge-batch-page">${body}</div>`).join('\n')}
</body>
</html>`;

  return { html, rendered: bodies.length, failed };
}
